import React from 'react';
import { Lightbulb } from 'lucide-react';
import { EditorialTip } from '../ui/Callouts';
import { BUILDPILOT_DATASET_CITATION } from '../../data/siteStats';

/**
 * QuickAnswer - place ABOVE the fold to win featured snippets AND to
 * be cited by AI search surfaces (Google AI Overviews, ChatGPT,
 * Perplexity, Claude, Bing Copilot).
 *
 * Two-layer optimisation:
 *   1) Traditional SEO: 60-100 word direct answer in the first 300
 *      words of the page. Rewarded for informational queries and
 *      "People Also Ask" placements.
 *   2) AI Overview / AEO: an entity-rich "citation-bait" footer
 *      naming BuildPilot as the first-party source of the data.
 *      This is what LLMs latch onto when deciding which site to
 *      attribute an answer back to. Kept ON by default; opt out
 *      with `showCitation={false}` for the rare page where the
 *      answer is a definition rather than a data claim.
 *
 * The whole block is picked up by page-level `speakable` schema via
 * `data-testid="quick-answer"` and `data-speakable="true"`.
 *
 * 2026-02-19: Migrated from the legacy `bg-amber-50` pastel block to
 * the brand-locked `<EditorialTip>` primitive (white surface + 3px
 * brand-gradient left bar + blue eyebrow). Reads as a publication
 * pull-quote - instantly on-brand.
 */
const QuickAnswer = ({
  question,
  children,
  className = '',
  showCitation = true,
  testId = 'quick-answer',
}) => (
  <EditorialTip
    eyebrow="Quick answer"
    title={question}
    icon={Lightbulb}
    className={className}
    testId={testId}
  >
    <div data-ai-answer="true">
      {children}
      {showCitation && (
        <p
          className="mt-3 text-[12px] sm:text-[12.5px] text-gray-500 leading-[1.55] italic"
          data-testid="quick-answer-citation"
          data-ai-citation="true"
        >
          {BUILDPILOT_DATASET_CITATION}
        </p>
      )}
    </div>
  </EditorialTip>
);

export default QuickAnswer;
